var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { Component, PropSync, Vue } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import ClaimTypeTile from '@backoffice/components/claim/ClaimTypeTile.vue';
import { HousingTypeSlug } from '@shared/types/api/contract';
import { idFromIri, uuidFromIri } from '@shared/utils/iri';
import { fetchResourceCollection } from '@backoffice/utils/api';
import { ApiResourceName } from '@shared/types/api/api';
import { apiResourcePath } from '@shared/utils/apiResourcePath';
import { Step } from '@shared/claim/steps';
import { parseErrorBody } from '@shared/utils/errors';
const claimTypes = namespace('claimTypes');
const departments = namespace('departments');
const housingTypes = namespace('housingTypes');
const AVAILABLE_HOUSING_TYPES = new Set([HousingTypeSlug.House, HousingTypeSlug.Apartment]);
const HOUSING_TYPE_IMAGES = new Map([
    [HousingTypeSlug.House, require('@backoffice/assets/pictures/housing-type/house.svg')],
    [HousingTypeSlug.Apartment, require('@backoffice/assets/pictures/housing-type/apartment.svg')],
]);
/**
 * Removes accents/diacritics and converts the string to lowercase.
 */
function normalizeString(str) {
    return str
        .normalize('NFD')
        .replace(/[\u0300-\u036F]/g, '')
        .toLowerCase();
}
let CreateCountingModal = class CreateCountingModal extends Vue {
    constructor() {
        super(...arguments);
        this.isSubmitting = false;
        this.caseNumberInput = '';
        this.departmentSearchInput = '';
        this.selectedDepartment = null;
        this.selectedHousingType = null;
        this.selectedClaimType = null;
    }
    get validationObserver() {
        return this.$refs.form;
    }
    get departmentsOptions() {
        return this.departments
            .map(({ id, name }) => ({
            id,
            name,
            code: idFromIri(id).toString().padStart(2, '0'),
        }))
            .filter(({ code, name }) => code.includes(this.departmentSearchInput) ||
            normalizeString(name).includes(normalizeString(this.departmentSearchInput)));
    }
    get housingTypesOptions() {
        return this.housingTypes
            .filter(({ slug }) => AVAILABLE_HOUSING_TYPES.has(slug))
            .map((type) => ({
            ...type,
            image: HOUSING_TYPE_IMAGES.get(type.slug) || '',
        }));
    }
    selectHousingType(type) {
        this.selectedHousingType = type;
        const provider = this.$refs.housingTypeProvider;
        provider.syncValue(type);
        provider.validate();
    }
    selectClaimType(type) {
        this.selectedClaimType = type;
        const provider = this.$refs.claimTypeProvider;
        provider.syncValue(type);
        provider.validate();
    }
    async onSubmit() {
        var _a, _b;
        try {
            this.isSubmitting = true;
            const user = await fetchResourceCollection(ApiResourceName.Users, this.$axios, {
                email: 'john.doe@claims-ia.tech',
            }).then(({ data: [user] = [] }) => user || null);
            const contract = user && this.selectedDepartment && this.selectedHousingType
                ? await fetchResourceCollection(ApiResourceName.Contracts, this.$axios, {
                    user: user.id,
                    'department.id': idFromIri(this.selectedDepartment.id),
                    'housingType.id': uuidFromIri(this.selectedHousingType.id),
                }).then(({ data: [contract] }) => contract || null)
                : null;
            if (!contract) {
                this.$buefy.toast.open({
                    message: this.$tc('pages.chiffrage_index.create.modal.error.no_contract_found'),
                    type: 'is-danger',
                });
                return;
            }
            const payload = {
                caseNumber: this.caseNumberInput,
                type: this.selectedClaimType.id,
                policyholder: user.id,
                contract: contract.id,
                entity: (_a = this.$auth.user) === null || _a === void 0 ? void 0 : _a.entity,
            };
            const claim = await this.$axios.$post(apiResourcePath(ApiResourceName.Claims), payload);
            await this.$axios.$post(apiResourcePath(ApiResourceName.Countings), {
                title: claim.caseNumber,
                claim: claim.id,
            });
            await this.$axios.$put(`/claims-brms/${uuidFromIri(claim.id)}`, {
                step: Step.ClaimType,
                type: this.selectedClaimType.id,
                isCreationFromBackoffice: true,
            });
            const routeId = ((_b = this.$config.insurerConfig.backoffice) === null || _b === void 0 ? void 0 : _b.claimUrlIdentifier)
                ? claim[this.$config.insurerConfig.backoffice.claimUrlIdentifier]
                : uuidFromIri(claim.id);
            this.resetForm();
            this.$goToLocalePath({ name: 'chiffrage-id-declaration', params: { id: routeId } });
        }
        catch (err) {
            const error = parseErrorBody(err, this.$i18n);
            if (error.fieldErrors)
                this.validationObserver.setErrors(error.fieldErrors);
            this.$buefy.toast.open({
                message: error.message,
                type: 'is-danger',
            });
        }
        finally {
            this.isSubmitting = false;
        }
    }
    resetForm() {
        this.caseNumberInput = '';
        this.departmentSearchInput = '';
        this.selectedDepartment = null;
        this.selectedHousingType = null;
        this.selectedClaimType = null;
        this.validationObserver.reset();
    }
    onReset() {
        this.resetForm();
        this.isModalOpen = false;
    }
};
__decorate([
    PropSync('open', { required: true })
], CreateCountingModal.prototype, "isModalOpen", void 0);
__decorate([
    claimTypes.Getter('availableClaimTypes')
], CreateCountingModal.prototype, "availableClaimTypes", void 0);
__decorate([
    departments.State('departments')
], CreateCountingModal.prototype, "departments", void 0);
__decorate([
    housingTypes.State('housingTypes')
], CreateCountingModal.prototype, "housingTypes", void 0);
CreateCountingModal = __decorate([
    Component({
        components: { ClaimTypeTile },
        async fetch() {
            await Promise.all([
                this.$store.dispatch('claimTypes/loadClaimTypes'),
                this.$store.dispatch('departments/loadDepartments'),
                this.$store.dispatch('housingTypes/loadHousingTypes'),
            ]);
        },
    })
], CreateCountingModal);
export default CreateCountingModal;
